import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  SmartToy as AgentIcon,
} from '@mui/icons-material';
import axios from 'axios';
import api from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface ApiKey {
  id: string;
  name: string;
  service: string;
  is_active: boolean;
  last_used_at?: string;
  expires_at?: string;
  created_at: string;
  description?: string;
}

interface WorkspaceUser {
  id: string;
  email: string;
  name: string;
  role_name: string;
  created_at: string;
}

interface UserInvitation {
  id: string;
  email: string;
  role_id: string;
  status: string;
  expires_at: string;
  created_at: string;
}

export default function Settings() {
  const [tabValue, setTabValue] = useState(0);
  
  // API Keys State
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [systemApiKeys, setSystemApiKeys] = useState<ApiKey[]>([]);
  const [showApiKeyDialog, setShowApiKeyDialog] = useState(false);
  const [showSystemApiKeyDialog, setShowSystemApiKeyDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState({
    name: '',
    service: '',
    api_key: '',
    api_secret: '',
    access_token: '',
  });
  const [newSystemApiKey, setNewSystemApiKey] = useState({
    service: '',
    api_key: '',
    api_secret: '',
    access_token: '',
    description: '',
  });
  
  // Workspace State
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceStats, setWorkspaceStats] = useState({
    total_users: 0,
    total_campaigns: 0,
    total_leads: 0,
  });
  
  // Users State
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [invitations, setInvitations] = useState<UserInvitation[]>([]);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [tabValue]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        // Load API Keys
        const response = await axios.get(`${API_URL}/api-keys`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setApiKeys(response.data);
      } else if (tabValue === 1) {
        // Load Workspace info
        const [wsResponse, statsResponse] = await Promise.all([
          axios.get(`${API_URL}/workspace`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }),
          axios.get(`${API_URL}/workspace/stats`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          })
        ]);
        setWorkspaceName(wsResponse.data.name);
        setWorkspaceStats(statsResponse.data);
      } else if (tabValue === 2) {
        // Load Users & Invitations
        const [usersResponse, invitationsResponse] = await Promise.all([
          axios.get(`${API_URL}/workspace/users`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          }),
          axios.get(`${API_URL}/workspace/users/invitations`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
          })
        ]);
        setUsers(usersResponse.data);
        setInvitations(invitationsResponse.data);
      } else if (tabValue === 3) {
        // Load System API Keys
        const response = await axios.get(`${API_URL}/api-keys/system`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        });
        setSystemApiKeys(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWorkspace = async () => {
    try {
      setError('');
      await api.patch('/workspace', { name: workspaceName });
      await loadData(); // Reload workspace data
      setSuccess('Workspace updated successfully');
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to update workspace';
      setError(errorMsg);
      console.error('Workspace save error:', err);
    }
  };

  const handleCreateApiKey = async () => {
    try {
      setError('');
      const credentials: any = {};
      if (newApiKey.api_key) credentials.api_key = newApiKey.api_key;
      if (newApiKey.api_secret) credentials.api_secret = newApiKey.api_secret;
      if (newApiKey.access_token) credentials.access_token = newApiKey.access_token;

      await api.post('/api-keys', {
        name: newApiKey.name,
        service: newApiKey.service,
        credentials,
        scopes: ['read', 'write', 'publish'],
      });

      setShowApiKeyDialog(false);
      setNewApiKey({ name: '', service: '', api_key: '', api_secret: '', access_token: '' });
      await loadData();
      setSuccess('API key created successfully');
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Failed to create API key';
      setError(errorMsg);
      console.error('API key creation error:', err);
    }
  };

  const handleDeleteApiKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;

    try {
      await axios.delete(`${API_URL}/api-keys/user/${keyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete API key');
    }
  };

  const handleCreateSystemApiKey = async () => {
    try {
      const credentials: any = {};
      if (newSystemApiKey.api_key) credentials.api_key = newSystemApiKey.api_key;
      if (newSystemApiKey.api_secret) credentials.api_secret = newSystemApiKey.api_secret;
      if (newSystemApiKey.access_token) credentials.access_token = newSystemApiKey.access_token;

      await axios.post(
        `${API_URL}/api-keys/system`,
        {
          service: newSystemApiKey.service,
          credentials,
          description: newSystemApiKey.description,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      setShowSystemApiKeyDialog(false);
      setNewSystemApiKey({ service: '', api_key: '', api_secret: '', access_token: '', description: '' });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create system API key');
    }
  };

  const handleDeleteSystemApiKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this system API key? This will affect all workspaces.')) return;

    try {
      await axios.delete(`${API_URL}/api-keys/system/${keyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete system API key');
    }
  };

  const handleInviteUser = async () => {
    try {
      await axios.post(
        `${API_URL}/workspace/users/invite`,
        {
          email: inviteEmail,
          role_id: inviteRole,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
        }
      );

      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteRole('');
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this user?')) return;

    try {
      await axios.delete(`${API_URL}/workspace/users/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove user');
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 0 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
        Settings
      </Typography>

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs 
        value={tabValue} 
        onChange={(e, v) => setTabValue(v)} 
        sx={{ 
          mb: { xs: 2, sm: 3 },
          '& .MuiTab-root': {
            minHeight: { xs: 48, sm: 64 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            px: { xs: 1, sm: 2 }
          }
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab 
          icon={<VpnKeyIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
          label="API Keys" 
          iconPosition="start" 
        />
        <Tab 
          icon={<BusinessIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
          label="Workspace" 
          iconPosition="start" 
        />
        <Tab 
          icon={<PeopleIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
          label="Team" 
          iconPosition="start" 
        />
        <Tab 
          icon={<VpnKeyIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
          label="System Keys" 
          iconPosition="start" 
        />
        <Tab 
          icon={<AgentIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />} 
          label="AI Agents" 
          iconPosition="start" 
        />
      </Tabs>

      {/* API Keys Tab */}
      {tabValue === 0 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            mb: { xs: 2, sm: 2 }
          }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Connected Integrations</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowApiKeyDialog(true)}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
            >
              Add API Key
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Name</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Service</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Last Used</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{key.name}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Chip label={key.service} size="small" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Chip
                        label={key.is_active ? 'Active' : 'Inactive'}
                        color={key.is_active ? 'success' : 'default'}
                        size="small"
                        sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {key.last_used_at
                        ? new Date(key.last_used_at).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteApiKey(key.id)}
                        sx={{ padding: { xs: '4px', sm: '8px' } }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Workspace Tab */}
      {tabValue === 1 && (
        <Box>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Workspace Information
                  </Typography>
                  <TextField
                    fullWidth
                    label="Workspace Name"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    margin="normal"
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    sx={{ mt: 2, width: { xs: '100%', sm: 'auto' } }} 
                    onClick={handleSaveWorkspace}
                    size="small"
                  >
                    Save Changes
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Usage Statistics
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography>Total Users: {workspaceStats.total_users}</Typography>
                    <Typography>Total Campaigns: {workspaceStats.total_campaigns}</Typography>
                    <Typography>Total Leads: {workspaceStats.total_leads}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Team Tab */}
      {tabValue === 2 && (
        <Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            mb: { xs: 2, sm: 2 }
          }}>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Team Members</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowInviteDialog(true)}
              size="small"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, width: { xs: '100%', sm: 'auto' } }}
            >
              Invite User
            </Button>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 3, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Name</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Email</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Role</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Joined</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.name}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{user.email}</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      <Chip label={user.role_name} size="small" sx={{ fontSize: { xs: '0.625rem', sm: '0.75rem' } }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveUser(user.id)}
                        disabled={user.role_name === 'super_admin'}
                        title={user.role_name === 'super_admin' ? 'Cannot delete super admin' : 'Remove user'}
                        sx={{ padding: { xs: '4px', sm: '8px' } }}
                      >
                        <DeleteIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {invitations.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, mt: { xs: 2, sm: 3 } }}>
                Pending Invitations
              </Typography>
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Email</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Status</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, display: { xs: 'none', sm: 'table-cell' } }}>Expires</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invitations.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell>{inv.email}</TableCell>
                        <TableCell>
                          <Chip label={inv.status} size="small" color="warning" />
                        </TableCell>
                        <TableCell>
                          {new Date(inv.expires_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* Add API Key Dialog */}
      <Dialog 
        open={showApiKeyDialog} 
        onClose={() => setShowApiKeyDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: '90vh', sm: '80vh' }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          pb: { xs: 1, sm: 2 }
        }}>
          Add API Key
        </DialogTitle>
        <DialogContent sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          overflowY: 'auto'
        }}>
          <TextField
            fullWidth
            label="Name"
            value={newApiKey.name}
            onChange={(e) => setNewApiKey({ ...newApiKey, name: e.target.value })}
            margin="normal"
            placeholder="Production LinkedIn Account"
            size="small"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Service</InputLabel>
            <Select
              value={newApiKey.service}
              onChange={(e) => setNewApiKey({ ...newApiKey, service: e.target.value })}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              <MenuItem value="linkedin" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>LinkedIn</MenuItem>
              <MenuItem value="twitter" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Twitter</MenuItem>
              <MenuItem value="facebook" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Facebook</MenuItem>
              <MenuItem value="instagram" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Instagram</MenuItem>
              <MenuItem value="hubspot" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>HubSpot</MenuItem>
              <MenuItem value="salesforce" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Salesforce</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="API Key"
            value={newApiKey.api_key}
            onChange={(e) => setNewApiKey({ ...newApiKey, api_key: e.target.value })}
            margin="normal"
            type="password"
            size="small"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
          />
          <TextField
            fullWidth
            label="API Secret (Optional)"
            value={newApiKey.api_secret}
            onChange={(e) => setNewApiKey({ ...newApiKey, api_secret: e.target.value })}
            margin="normal"
            type="password"
            size="small"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
          />
          <TextField
            fullWidth
            label="Access Token (Optional)"
            value={newApiKey.access_token}
            onChange={(e) => setNewApiKey({ ...newApiKey, access_token: e.target.value })}
            margin="normal"
            type="password"
            size="small"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setShowApiKeyDialog(false)}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateApiKey} 
            variant="contained"
            disabled={!newApiKey.name || !newApiKey.service || !newApiKey.api_key}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog 
        open={showInviteDialog} 
        onClose={() => setShowInviteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: '90vh', sm: '80vh' }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          pb: { xs: 1, sm: 2 }
        }}>
          Invite Team Member
        </DialogTitle>
        <DialogContent sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          overflowY: 'auto'
        }}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            margin="normal"
            size="small"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Role</InputLabel>
            <Select 
              value={inviteRole} 
              onChange={(e) => setInviteRole(e.target.value)}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              <MenuItem value="marketer" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Marketer</MenuItem>
              <MenuItem value="sdr" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>SDR</MenuItem>
              <MenuItem value="analyst" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Analyst</MenuItem>
              <MenuItem value="admin" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setShowInviteDialog(false)}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleInviteUser} 
            variant="contained"
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* System API Keys Tab (Admin Only) */}
      {tabValue === 3 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            System API Keys are shared across all workspaces. Changes here affect the entire platform.
          </Alert>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">System-Level API Keys</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowSystemApiKeyDialog(true)}
            >
              Add System API Key
            </Button>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary" paragraph>
                Configure API keys for system-wide services:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li><strong>Gemini API</strong>: For AI content generation (required)</li>
                <li><strong>SendGrid</strong>: For email campaigns and notifications</li>
                <li><strong>Clearbit</strong>: For lead enrichment</li>
                <li><strong>Lusha</strong>: For lead sourcing and enrichment (prospecting)</li>
                <li><strong>LinkdAPI</strong>: For LinkedIn data extraction (profiles, search)</li>
                <li><strong>SerperDev</strong>: For web search and research</li>
                <li><strong>Gomens</strong>: For automated blog posting to Gomens platform</li>
              </Typography>
            </CardContent>
          </Card>

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Used</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {systemApiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {key.service.charAt(0).toUpperCase() + key.service.slice(1)}
                      </Typography>
                    </TableCell>
                    <TableCell>{key.description || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={key.is_active ? 'Active' : 'Inactive'} 
                        color={key.is_active ? 'success' : 'default'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteSystemApiKey(key.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {systemApiKeys.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No system API keys configured yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* System API Key Dialog */}
      <Dialog 
        open={showSystemApiKeyDialog} 
        onClose={() => setShowSystemApiKeyDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxHeight: { xs: '95vh', sm: '85vh' }
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: { xs: '1.25rem', sm: '1.5rem' },
          pb: { xs: 1, sm: 2 }
        }}>
          Add System API Key
        </DialogTitle>
        <DialogContent sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
          overflowY: 'auto'
        }}>
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Service</InputLabel>
            <Select
              value={newSystemApiKey.service}
              onChange={(e) => setNewSystemApiKey({ ...newSystemApiKey, service: e.target.value })}
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              <MenuItem value="gemini" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Gemini API (AI Content Generation)</MenuItem>
              <MenuItem value="sendgrid" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>SendGrid (Email Service)</MenuItem>
              <MenuItem value="clearbit" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Clearbit (Lead Enrichment)</MenuItem>
              <MenuItem value="lusha" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Lusha (Lead Sourcing & Enrichment)</MenuItem>
              <MenuItem value="linkdapi" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>LinkdAPI (LinkedIn Data Extraction)</MenuItem>
              <MenuItem value="serper" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>SerperDev (Web Search)</MenuItem>
              <MenuItem value="openai" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>OpenAI (Embeddings)</MenuItem>
              <MenuItem value="hubspot" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>HubSpot (CRM Integration)</MenuItem>
              <MenuItem value="gomens" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Gomens (Blog Posting Platform)</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="API Key"
            value={newSystemApiKey.api_key}
            onChange={(e) => setNewSystemApiKey({ ...newSystemApiKey, api_key: e.target.value })}
            margin="normal"
            type="password"
            size="small"
            helperText="This key will be encrypted and used across all workspaces"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiFormHelperText-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
          <TextField
            fullWidth
            label="API Secret (Optional)"
            value={newSystemApiKey.api_secret}
            onChange={(e) => setNewSystemApiKey({ ...newSystemApiKey, api_secret: e.target.value })}
            margin="normal"
            type="password"
            size="small"
            helperText="Required for some services like OAuth integrations"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiFormHelperText-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
          <TextField
            fullWidth
            label="Access Token (Optional)"
            value={newSystemApiKey.access_token}
            onChange={(e) => setNewSystemApiKey({ ...newSystemApiKey, access_token: e.target.value })}
            margin="normal"
            type="password"
            size="small"
            helperText="OAuth access token if applicable"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiFormHelperText-root': { fontSize: { xs: '0.75rem', sm: '0.875rem' } }
            }}
          />
          <TextField
            fullWidth
            label="Description (Optional)"
            value={newSystemApiKey.description}
            onChange={(e) => setNewSystemApiKey({ ...newSystemApiKey, description: e.target.value })}
            margin="normal"
            multiline
            rows={2}
            size="small"
            placeholder="e.g., Production Gemini API key"
            sx={{ 
              '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } },
              '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setShowSystemApiKeyDialog(false)}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateSystemApiKey}
            variant="contained"
            disabled={!newSystemApiKey.service || !newSystemApiKey.api_key}
            sx={{ 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' }
            }}
          >
            Add System Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Agents Tab */}
      {tabValue === 4 && (
        <Box>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Multi-Agent System Configuration
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Your marketing system uses 19 specialized AI agents coordinated by an orchestrator. 
              Each agent uses the <strong>ReAct (Reason + Act)</strong> pattern for autonomous decision-making.
              External APIs are registered as intelligent tools that agents use strategically.
            </Typography>
            <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                🚀 Version 2.0.1 - Intelligent Agent Architecture
              </Typography>
              <Typography variant="body2">
                All agents now feature autonomous reasoning capabilities. Instead of simple API calls, 
                agents think through problems, select appropriate tools, observe results, and adapt their strategies.
                External APIs (Lusha, LinkdAPI, SerperDev, HubSpot, etc.) serve as tools agents intelligently orchestrate.
              </Typography>
            </Alert>
          </Box>

          <Grid container spacing={3}>
            {/* Orchestrator Agent */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="primary" />
                    <Typography variant="h6">Orchestrator Agent</Typography>
                  </Box>
                  <Chip label="Core System" color="primary" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Central coordinator that delegates tasks to specialized agents using AutoGen and Gemini AI.
                    Routes all tasks through the unified execute_task() interface.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: Multi-agent coordination
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Lead Management Agents */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Lead Management Agents
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">ICP Strategy Agent</Typography>
                  </Box>
                  <Chip label="Lead Management" color="info" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Analyzes and defines Ideal Customer Profiles using ReAct reasoning with 9 intelligent tools.
                    Autonomously researches markets, analyzes competitors, and validates ICPs.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: 9 (market research, competitor analysis, validation)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Lead Sourcing Agent</Typography>
                  </Box>
                  <Chip label="Lead Management" color="info" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Intelligently discovers leads using 6 tools including Lusha, LinkdAPI, and SerperDev.
                    Reasons about search strategies and lead quality before taking action.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Lusha, LinkdAPI, SerperDev | Tools: 6 (search, enrichment, validation)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Lead Enrichment Agent</Typography>
                  </Box>
                  <Chip label="Lead Management" color="info" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Strategically enriches leads with 8 intelligent tools for company data, social profiles, and firmographics.
                    Selects optimal enrichment sources based on data quality and coverage.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Lusha, LinkdAPI | Tools: 8 (company lookup, contact enrichment, verification)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Lead Scoring Agent</Typography>
                  </Box>
                  <Chip label="Lead Management" color="info" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Intelligently scores leads using 7 tools for fit, intent, and engagement analysis.
                    Reasons through scoring criteria and adapts to your ICP definitions.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: 7 (scoring models, ICP matching, prioritization)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Campaign & Content Agents */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Campaign & Content Agents
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Campaign Planner Agent</Typography>
                  </Box>
                  <Chip label="Campaign Management" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Strategically plans campaigns using 8 intelligent tools for goals, budgets, and channel selection.
                    Reasons through campaign objectives and market conditions autonomously.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: 8 (planning, budgeting, channel optimization)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Content Architect Agent</Typography>
                  </Box>
                  <Chip label="Content Creation" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Designs content calendars with topics, formats, and publishing schedules.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Content Generation Agent</Typography>
                  </Box>
                  <Chip label="Content Creation" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Creates content using 7 intelligent tools for research, writing, and optimization.
                    Thinks through content strategy, audience needs, and brand voice before generating.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: 7 (research, writing, SEO, tone analysis)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Compliance Agent</Typography>
                  </Box>
                  <Chip label="Content Creation" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Reviews content for brand safety, legal compliance, and GDPR adherence.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Creative Media Agent</Typography>
                  </Box>
                  <Chip label="Content Creation" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Generates images and video storyboards for visual content needs.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Repurposing Agent</Typography>
                  </Box>
                  <Chip label="Content Creation" color="success" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Transforms content across formats (blog to tweets, video to article, etc.).
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Publishing & Engagement Agents */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Publishing & Engagement Agents
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Posting Agent</Typography>
                  </Box>
                  <Chip label="Publishing" color="warning" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Publishes content to LinkedIn, Twitter, and other social platforms.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: LinkedIn API Key, Twitter API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Engagement Agent</Typography>
                  </Box>
                  <Chip label="Publishing" color="warning" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Monitors conversations, suggests replies, and manages social engagement.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: LinkedIn API Key, Twitter API Key, Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Analytics & Optimization Agents */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Analytics & Optimization Agents
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Analytics Agent</Typography>
                  </Box>
                  <Chip label="Analytics" color="error" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Analyzes performance using 9 intelligent tools for metrics, insights, and reporting.
                    Autonomously identifies trends, anomalies, and optimization opportunities.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: 9 (metrics, reporting, forecasting, insights)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Experimentation Agent</Typography>
                  </Box>
                  <Chip label="Analytics" color="error" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Designs and analyzes A/B tests to optimize campaign performance.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Integration & System Agents */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Integration & System Agents
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">CRM Integration Agent</Typography>
                  </Box>
                  <Chip label="Integration" color="default" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Intelligently syncs data using 8 tools for HubSpot and Salesforce integration.
                    Reasons about data mapping, conflict resolution, and sync strategies.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: HubSpot/Salesforce | Tools: 8 (contact sync, deal management, activities)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Knowledge Base Agent</Typography>
                  </Box>
                  <Chip label="System" color="default" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    RAG-based agent providing access to organizational knowledge and documents.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Data Ingestion Agent</Typography>
                  </Box>
                  <Chip label="System" color="default" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Processes external data using 8 intelligent tools for CSV, API, and webhook ingestion.
                    Autonomously validates, maps, and transforms data for optimal quality.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: Gemini API Key | Tools: 8 (CSV import, validation, mapping, transformation)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <AgentIcon color="secondary" />
                    <Typography variant="h6">Admin Agent</Typography>
                  </Box>
                  <Chip label="System" color="default" size="small" sx={{ mb: 1 }} />
                  <Typography variant="body2" color="textSecondary" paragraph>
                    Manages workspace settings, user roles, and system governance.
                  </Typography>
                  <Typography variant="caption" display="block" color="textSecondary">
                    Required: None (uses database only)
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              ReAct Pattern & Tool Architecture
            </Typography>
            <Typography variant="body2">
              <strong>What's Different in v2.0.1:</strong><br />
              • All 17 agents use <strong>ReAct (Reason + Act)</strong> pattern with autonomous decision-making<br />
              • External APIs (Lusha, LinkdAPI, SerperDev, HubSpot, etc.) are registered as <strong>intelligent tools</strong><br />
              • Agents <strong>think → act → observe → adapt</strong> in a continuous reasoning loop<br />
              • 136 total tools across all agents for maximum flexibility<br />
              • Full backward compatibility maintained with existing workflows<br />
              <br />
              <strong>API Key Requirements:</strong><br />
              • <strong>Gemini AI</strong>: Required for agent reasoning and orchestration<br />
              • <strong>Lusha + LinkdAPI</strong>: Required for lead sourcing and enrichment tools<br />
              • <strong>SerperDev</strong>: Required for web search and research tools<br />
              • <strong>LinkedIn/Twitter</strong>: Required for social media publishing and engagement tools<br />
              • <strong>HubSpot/Salesforce</strong>: Required for CRM integration tools<br />
              <br />
              Configure these keys in the <strong>System API Keys</strong> tab above.
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
}
